// Typed Policy AI client (APP-004 R2). Mirrors the Odingard Studio Policy AI
// OpenAPI contract for the surfaces the Studio module uses. All calls go
// through the Studio Worker proxy (`/organizations/{orgId}/policy-ai/...`),
// which is the authorization + entitlement boundary; the browser never talks to
// the Policy AI backend directly and never holds a service credential.
//
// External review is the one exception: those callers are not Studio users, so
// they present a time-bound Policy AI review-link token to the unauthenticated
// `/policy-ai/external-review/...` edge routes.
import { ApiError } from "./types";

export interface PolicyResponse<T> {
  data: T;
  demoMode: boolean;
  correlationId: string | null;
}

export interface PolicyClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

// ----- Lifecycle -----------------------------------------------------------
export interface PolicyLifecycleDashboard {
  policies: { total: number; active: number; reviewDueSoon: number; reviewOverdue: number };
  versions: { byStatus: Record<string, number> };
  publications: { byStatus: Record<string, number> };
  acknowledgments: {
    openCampaigns: number;
    pending: number;
    acknowledged: number;
    refused: number;
    overdue: number;
  };
  exceptions: { requested: number; approved: number; expired: number; expiringSoon: number };
  tasks: { open: number; overdue: number };
}

export type LifecycleTaskKind = "policy_review" | "exception_expiry" | "acknowledgment_deadline";
export type LifecycleTaskStatus = "open" | "completed" | "cancelled";

export interface LifecycleTask {
  id: string;
  kind: LifecycleTaskKind;
  status: LifecycleTaskStatus;
  subjectId: string;
  policyId: string | null;
  dueAt: string;
  notifiedAt: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface LifecycleRunResult {
  tasksCreated: number;
  reviewsDue: number;
  exceptionsExpired: number;
  acknowledgmentsOverdue: number;
  remindersSent: number;
}

// ----- Registry, drafts, versions ------------------------------------------
export interface Policy {
  id: string;
  title: string;
  description: string | null;
  referenceCode: string | null;
  status: string;
  ownerId: string | null;
  currentVersionId: string | null;
  effectiveDate: string | null;
  reviewDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyDraftSection {
  id: string;
  draftId: string;
  sectionKey: string;
  heading: string;
  body: string;
  position: number;
  aiGenerated: boolean;
  sourceContentId: string | null;
}

export interface PolicyDraft {
  id: string;
  policyId: string | null;
  title: string;
  policyType: string | null;
  audience: string | null;
  scope: string | null;
  summary: string;
  status: string;
  sourceKind: string;
  ownerId: string | null;
  effectiveDate: string | null;
  reviewIntervalMonths: number | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyDraftDetail extends PolicyDraft {
  sections: PolicyDraftSection[];
}

export interface PolicyVersion {
  id: string;
  policyId: string;
  draftId: string | null;
  versionNumber: number;
  title: string;
  summary: string;
  status: string;
  supersedesVersionId: string | null;
  contentHash: string;
  createdBy: string;
  createdAt: string;
}

export interface CreateDraftInput {
  title: string;
  policyId?: string;
  policyType?: string;
  audience?: string;
  scope?: string;
  summary?: string;
  effectiveDate?: string;
  reviewIntervalMonths?: number;
  sourceKind?: "blank" | "template" | "existing_version";
  sourceTemplateId?: string;
  sourceVersionId?: string;
}

export interface AddSectionInput {
  sectionKey: string;
  heading: string;
  body?: string;
  aiGenerated?: boolean;
}

export interface SubmitReviewInput {
  reviewerIds: string[];
  requiredReviewerIds?: string[] | null;
  mode?: "sequential" | "parallel";
  dueInDays?: number | null;
}

export interface PublishInput {
  effectiveDate?: string | null;
  scheduledFor?: string | null;
  distribution?: { userIds?: string[]; groupIds?: string[]; channel?: string };
  notify?: boolean;
}

export interface PolicyReview {
  id: string;
  versionId: string;
  status: string;
  mode: string;
  currentStep: number;
  submittedBy: string;
  dueAt: string | null;
  createdAt: string;
}

export interface ReviewAssignment {
  id: string;
  reviewId: string;
  reviewerId: string;
  step: number;
  required: boolean;
  status: string;
  dueAt: string | null;
}

export interface ReviewDecision {
  id: string;
  reviewId: string;
  reviewerId: string;
  step: number;
  decision: string;
  rationale: string;
  decidedAt: string;
}

export interface PolicyReviewDetail extends PolicyReview {
  assignments: ReviewAssignment[];
  decisions: ReviewDecision[];
}

export interface PolicyApproval {
  id: string;
  versionId: string;
  approverId: string;
  rationale: string;
  contentHash: string;
  evidenceHash: string;
  approvedAt: string;
}

export interface PolicyPublication {
  id: string;
  versionId: string;
  policyId: string;
  approvalId: string;
  status: string;
  effectiveDate: string | null;
  scheduledFor: string | null;
}

// ----- Acknowledgments ------------------------------------------------------
export type CampaignStatus = "open" | "closed" | "cancelled";

export interface AcknowledgmentCampaign {
  id: string;
  versionId: string;
  policyId: string;
  name: string;
  instructions: string;
  status: CampaignStatus;
  deadlineAt: string;
  reminderDays: number[];
  requireRefusalReason: boolean;
  createdBy: string;
  createdAt: string;
  closedAt: string | null;
}

export interface CampaignTotals {
  assigned: number;
  pending: number;
  acknowledged: number;
  refused: number;
  overdue: number;
}

export type AcknowledgmentStatus = "pending" | "acknowledged" | "refused" | "reassigned";

export interface PolicyAcknowledgment {
  id: string;
  campaignId: string;
  versionId: string;
  recipientId: string;
  audienceKind: "user" | "group";
  audienceId: string;
  status: AcknowledgmentStatus;
  note: string | null;
  refusalReason: string | null;
  evidenceHash: string | null;
  overdueAt: string | null;
  lastRemindedAt: string | null;
  completedAt: string | null;
  reassignedFrom: string | null;
  reassignedTo: string | null;
  createdAt: string;
}

export interface CreateCampaignInput {
  name: string;
  instructions?: string;
  deadlineAt: string;
  reminderDays?: number[];
  requireRefusalReason?: boolean;
  recipients: { userId: string; groupId?: string | null }[];
}

// ----- Exceptions -----------------------------------------------------------
export type ExceptionStatus =
  | "requested"
  | "changes_requested"
  | "approved"
  | "rejected"
  | "expired"
  | "closed";

export interface PolicyException {
  id: string;
  policyId: string;
  versionId: string | null;
  sectionKey: string | null;
  title: string;
  businessJustification: string;
  scope: string;
  affectedSystems: string[];
  riskDescription: string;
  compensatingControls: string;
  riskOwnerId: string;
  requestedBy: string;
  status: ExceptionStatus;
  requestedStart: string;
  requestedEnd: string;
  approvedStart: string | null;
  approvedEnd: string | null;
  conditions: string;
  evidenceRefs: string[];
  renewedFromId: string | null;
  createdAt: string;
  updatedAt: string;
  expiredAt: string | null;
  closedAt: string | null;
  closedBy: string | null;
}

export interface ExceptionDecisionRecord {
  id: string;
  exceptionId: string;
  approverId: string;
  decision: string;
  rationale: string;
  approvedStart: string | null;
  approvedEnd: string | null;
  conditions: string;
  decidedAt: string;
}

export interface PolicyExceptionDetail extends PolicyException {
  decisions: ExceptionDecisionRecord[];
}

export interface RequestExceptionInput {
  policyId: string;
  versionId?: string | null;
  sectionKey?: string | null;
  title: string;
  businessJustification: string;
  scope?: string;
  affectedSystems?: string[];
  riskDescription?: string;
  compensatingControls?: string;
  riskOwnerId: string;
  requestedStart: string;
  requestedEnd: string;
  evidenceRefs?: string[];
}

export interface ExceptionDecisionInput {
  decision: "approved" | "rejected" | "changes_requested";
  rationale?: string;
  approvedStart?: string | null;
  approvedEnd?: string | null;
  conditions?: string;
}

// ----- External review ------------------------------------------------------
export interface ExternalReviewer {
  id: string;
  draftId: string;
  email: string;
  displayName: string;
  canComment: boolean;
  status: string;
  invitedBy: string;
  expiresAt: string;
  recommendation: string | null;
  reviewNote: string | null;
  submittedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface ExternalInvitation {
  reviewer: ExternalReviewer;
  /** Clear-text link token — returned exactly once, never stored server-side. */
  token: string;
}

export interface ExternalDraftView {
  reviewer: ExternalReviewer;
  draft: PolicyDraft;
  sections: PolicyDraftSection[];
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function raise(status: number, data: unknown): never {
  // Preserve the Policy AI structured error envelope { error: { code, message } }.
  const env = (data ?? {}) as { error?: { code?: string; message?: string } | string };
  if (env.error && typeof env.error === "object") {
    throw new ApiError(status, env.error.code ?? "error", env.error.message ?? `Request failed (${status}).`);
  }
  const flat = (data ?? {}) as { error?: string; message?: string };
  throw new ApiError(status, flat.error ?? "error", flat.message ?? `Request failed (${status}).`);
}

export class PolicyAiClient {
  private baseUrl: string;
  private getToken: () => string | null;
  private onUnauthorized: (() => void) | undefined;

  constructor(opts: PolicyClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
  }

  private async request<T>(
    method: string,
    orgId: string,
    subpath: string,
    body?: unknown,
  ): Promise<PolicyResponse<T>> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    const path = `/organizations/${orgId}/policy-ai/${subpath.replace(/^\/+/, "")}`;
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    const data = await parse(res);
    if (!res.ok) raise(res.status, data);
    return {
      data: data as T,
      demoMode: res.headers.get("x-policy-ai-mode") === "demo",
      correlationId: res.headers.get("x-correlation-id"),
    };
  }

  /** Token-scoped external review call — carries the review link token, not a session. */
  private async external<T>(method: string, action: string, linkToken: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      Authorization: `Bearer ${linkToken}`,
    };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}/policy-ai/external-review/${action}`, init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    const data = await parse(res);
    if (!res.ok) raise(res.status, data);
    return data as T;
  }

  // ----- Lifecycle ---------------------------------------------------------
  dashboard(orgId: string) {
    return this.request<{ dashboard: PolicyLifecycleDashboard }>("GET", orgId, "policy-lifecycle/dashboard");
  }
  tasks(orgId: string, filter: { status?: LifecycleTaskStatus; kind?: LifecycleTaskKind } = {}) {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.kind) q.set("kind", filter.kind);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return this.request<{ tasks: LifecycleTask[] }>("GET", orgId, `policy-lifecycle/tasks${suffix}`);
  }
  closeTask(orgId: string, taskId: string, status: "completed" | "cancelled" = "completed") {
    return this.request<{ task: LifecycleTask }>("POST", orgId, `policy-lifecycle/tasks/${taskId}/closure`, {
      status,
    });
  }
  runLifecycle(orgId: string) {
    return this.request<{ result: LifecycleRunResult }>("POST", orgId, "policy-lifecycle/run", {});
  }

  // ----- Registry, drafts, versions ----------------------------------------
  policies(orgId: string) {
    return this.request<{ policies: Policy[] }>("GET", orgId, "policies");
  }
  createPolicy(orgId: string, input: { title: string; description?: string; referenceCode?: string }) {
    return this.request<{ policy: Policy }>("POST", orgId, "policies", input);
  }
  drafts(orgId: string) {
    return this.request<{ drafts: PolicyDraft[] }>("GET", orgId, "policy-drafts");
  }
  draft(orgId: string, draftId: string) {
    return this.request<{ draft: PolicyDraftDetail }>("GET", orgId, `policy-drafts/${draftId}`);
  }
  createDraft(orgId: string, input: CreateDraftInput) {
    return this.request<{ draft: PolicyDraft }>("POST", orgId, "policy-drafts", input);
  }
  addSection(orgId: string, draftId: string, input: AddSectionInput) {
    return this.request<{ section: PolicyDraftSection }>(
      "POST",
      orgId,
      `policy-drafts/${draftId}/sections`,
      input,
    );
  }
  createVersion(orgId: string, draftId: string) {
    return this.request<{ version: PolicyVersion }>("POST", orgId, `policy-drafts/${draftId}/versions`, {});
  }
  versions(orgId: string, policyId: string) {
    return this.request<{ versions: PolicyVersion[] }>(
      "GET",
      orgId,
      `policy-versions?policyId=${encodeURIComponent(policyId)}`,
    );
  }
  submitForReview(orgId: string, versionId: string, input: SubmitReviewInput) {
    return this.request<{ review: PolicyReview }>("POST", orgId, `policy-versions/${versionId}/submit`, input);
  }
  reviews(orgId: string, versionId: string) {
    return this.request<{ reviews: PolicyReview[] }>(
      "GET",
      orgId,
      `policy-reviews?versionId=${encodeURIComponent(versionId)}`,
    );
  }
  review(orgId: string, reviewId: string) {
    return this.request<{ review: PolicyReviewDetail }>("GET", orgId, `policy-reviews/${reviewId}`);
  }
  recordReviewDecision(
    orgId: string,
    reviewId: string,
    input: { decision: "approved" | "changes_requested" | "rejected"; rationale?: string },
  ) {
    return this.request<{ review: PolicyReviewDetail }>(
      "POST",
      orgId,
      `policy-reviews/${reviewId}/decisions`,
      input,
    );
  }
  approveVersion(orgId: string, versionId: string, rationale = "") {
    return this.request<{ approval: PolicyApproval }>("POST", orgId, `policy-versions/${versionId}/approve`, {
      rationale,
    });
  }
  publishVersion(orgId: string, versionId: string, input: PublishInput = {}) {
    return this.request<{ publication: PolicyPublication }>(
      "POST",
      orgId,
      `policy-versions/${versionId}/publish`,
      input,
    );
  }

  // ----- Acknowledgments ---------------------------------------------------
  campaigns(orgId: string, status?: CampaignStatus) {
    const suffix = status ? `?status=${status}` : "";
    return this.request<{ campaigns: AcknowledgmentCampaign[] }>(
      "GET",
      orgId,
      `policy-acknowledgment-campaigns${suffix}`,
    );
  }
  campaign(orgId: string, campaignId: string) {
    return this.request<{ campaign: AcknowledgmentCampaign; totals: CampaignTotals }>(
      "GET",
      orgId,
      `policy-acknowledgment-campaigns/${campaignId}`,
    );
  }
  campaignEvidence(orgId: string, campaignId: string) {
    return this.request<{ acknowledgments: PolicyAcknowledgment[] }>(
      "GET",
      orgId,
      `policy-acknowledgment-campaigns/${campaignId}/acknowledgments`,
    );
  }
  createCampaign(orgId: string, versionId: string, input: CreateCampaignInput) {
    return this.request<{ campaign: AcknowledgmentCampaign }>(
      "POST",
      orgId,
      `policy-versions/${versionId}/acknowledgment-campaigns`,
      input,
    );
  }
  sendReminders(orgId: string, campaignId: string) {
    return this.request<{ reminded: number }>(
      "POST",
      orgId,
      `policy-acknowledgment-campaigns/${campaignId}/reminders`,
      {},
    );
  }
  closeCampaign(orgId: string, campaignId: string, status: "closed" | "cancelled" = "closed") {
    return this.request<{ campaign: AcknowledgmentCampaign }>(
      "POST",
      orgId,
      `policy-acknowledgment-campaigns/${campaignId}/close`,
      { status },
    );
  }
  myAcknowledgments(orgId: string) {
    return this.request<{ acknowledgments: PolicyAcknowledgment[] }>("GET", orgId, "policy-acknowledgments");
  }
  completeAcknowledgment(
    orgId: string,
    acknowledgmentId: string,
    input: { decision: "acknowledged" | "refused"; note?: string | null; refusalReason?: string | null },
  ) {
    return this.request<{ acknowledgment: PolicyAcknowledgment }>(
      "POST",
      orgId,
      `policy-acknowledgments/${acknowledgmentId}/completion`,
      input,
    );
  }

  // ----- Exceptions --------------------------------------------------------
  exceptions(orgId: string, filter: { status?: ExceptionStatus; policyId?: string } = {}) {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.policyId) q.set("policyId", filter.policyId);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return this.request<{ exceptions: PolicyException[] }>("GET", orgId, `policy-exceptions${suffix}`);
  }
  exception(orgId: string, exceptionId: string) {
    return this.request<{ exception: PolicyExceptionDetail }>(
      "GET",
      orgId,
      `policy-exceptions/${exceptionId}`,
    );
  }
  requestException(orgId: string, input: RequestExceptionInput) {
    return this.request<{ exception: PolicyException }>("POST", orgId, "policy-exceptions", input);
  }
  decideException(orgId: string, exceptionId: string, input: ExceptionDecisionInput) {
    return this.request<{ exception: PolicyExceptionDetail }>(
      "POST",
      orgId,
      `policy-exceptions/${exceptionId}/decisions`,
      input,
    );
  }
  renewException(orgId: string, exceptionId: string, window: { requestedStart: string; requestedEnd: string }) {
    return this.request<{ exception: PolicyException }>(
      "POST",
      orgId,
      `policy-exceptions/${exceptionId}/renewals`,
      window,
    );
  }
  closeException(orgId: string, exceptionId: string, rationale = "") {
    return this.request<{ exception: PolicyException }>(
      "POST",
      orgId,
      `policy-exceptions/${exceptionId}/closure`,
      { rationale },
    );
  }

  // ----- External review ---------------------------------------------------
  externalReviewers(orgId: string, draftId: string) {
    return this.request<{ reviewers: ExternalReviewer[] }>(
      "GET",
      orgId,
      `policy-drafts/${draftId}/external-reviewers`,
    );
  }
  inviteExternalReviewer(
    orgId: string,
    draftId: string,
    input: { email: string; displayName?: string; canComment?: boolean; expiresAt: string },
  ) {
    return this.request<ExternalInvitation>(
      "POST",
      orgId,
      `policy-drafts/${draftId}/external-reviewers`,
      input,
    );
  }
  revokeExternalReviewer(orgId: string, reviewerId: string) {
    return this.request<{ reviewer: ExternalReviewer }>(
      "DELETE",
      orgId,
      `policy-external-reviewers/${reviewerId}`,
    );
  }
  externalDraft(linkToken: string) {
    return this.external<ExternalDraftView>("GET", "draft", linkToken);
  }
  externalComment(linkToken: string, body: string, sectionId: string | null = null) {
    return this.external<{ comment: { id: string; body: string } }>("POST", "comments", linkToken, {
      body,
      sectionId,
    });
  }
  externalSubmit(linkToken: string, recommendation: "approve" | "changes_requested", reviewNote = "") {
    return this.external<{ reviewer: ExternalReviewer }>("POST", "submission", linkToken, {
      recommendation,
      reviewNote,
    });
  }
}
